import { RunDto, RunResultsHistoricAggregate } from "@custom-types/modelRun"
import ChartDataLabels from "chartjs-plugin-datalabels"
import zoomPlugin from "chartjs-plugin-zoom"
import { Bar } from "react-chartjs-2"
import { useEffect, useMemo, useRef } from "react"
import { Box, CircularProgress, Grid, Typography } from "@mui/material"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { useChartDensity } from "@features/results/Result/ChartsContainer/useChartDensity"
import { useBarStringsChartData } from "@features/results/Result/ChartsContainer/useBarStringsChartData"
import { Chart, ChartOptions } from "chart.js"
import { BarStringsChartTable } from "@features/results/Result/ChartsContainer/BarStringsChartTable"
import { createDashboardBarStringsChartOptions } from "@features/results/Result/Dashboard/DashboardBarStringsChart.utils"

export type DashboardBarStringsChartProps = {
  aggregateData?: RunResultsHistoricAggregate
  nodes: GroupNodeLabel[] | null
  lagShift: number
  isLoading?: boolean
  runs: RunDto[]
  isStacked: boolean
  showAsPercentage?: boolean
  showAsTable?: boolean
  showLegend?: boolean
  legendPosition?: "top" | "bottom"
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
  onResetZoomReady?: (handler: () => void) => void
}

const MAX_BARS_FOR_DEFAULT_LABELS = 100
const CATEGORICAL_TABLE_FILE_NAME = "categorical-samples-table"

export const DashboardBarStringsChart = ({
  aggregateData,
  nodes,
  lagShift,
  isLoading,
  runs,
  isStacked,
  showAsPercentage = true,
  showAsTable,
  showLegend,
  legendPosition = "top",
  hiddenDatasets,
  onLegendClick,
  onResetZoomReady,
}: DashboardBarStringsChartProps) => {
  const chartRef = useRef<Chart<"bar">>(null)

  const { timestepLabels, chartData, dataSets } = useBarStringsChartData({
    aggregateData,
    lagShift,
    runs,
    isStacked,
    showAsPercentage,
  })

  const { shouldShowLabels } = useChartDensity({
    datasets: chartData.datasets,
    maxBarsForDefaultLabels: MAX_BARS_FOR_DEFAULT_LABELS,
  })

  const chartDataWithVisibility = useMemo(
    () => ({
      ...chartData,
      datasets: chartData.datasets.map((dataset) => ({
        ...dataset,
        hidden: hiddenDatasets?.includes(dataset.label || "") ?? false,
      })),
    }),
    [chartData, hiddenDatasets],
  )

  const options = useMemo<ChartOptions<"bar">>(
    () =>
      createDashboardBarStringsChartOptions({
        isStacked,
        showAsPercentage,
        shouldShowLabels,
        showLegend,
        legendPosition,
        timestepLabels,
        onLegendClick,
      }),
    [
      isStacked,
      onLegendClick,
      showAsPercentage,
      shouldShowLabels,
      showLegend,
      legendPosition,
      timestepLabels,
    ],
  )

  const nodesLabel = useMemo(
    () =>
      nodes && nodes.length > 1
        ? nodes.map((node) => node.label).join(", ")
        : nodes?.[0]?.label || "",
    [nodes],
  )

  useEffect(() => {
    onResetZoomReady?.(() => {
      if (chartRef && chartRef.current) {
        chartRef.current.resetZoom()
      }
    })
  }, [onResetZoomReady])

  return (
    <>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid item xs={12} sx={{ height: "100%" }}>
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 260 }}>
              <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
                {showAsPercentage ? "100% " : ""}
                {isStacked ? "Stacked" : "Grouped"} Bar Chart <b>{nodesLabel}</b>
              </Typography>
              {showAsTable ? (
                <BarStringsChartTable
                  dataSets={dataSets}
                  timestepLabels={timestepLabels}
                  exportFileName={CATEGORICAL_TABLE_FILE_NAME}
                />
              ) : (
                <Box sx={{ flex: 1, minHeight: 220 }}>
                  <Bar
                    options={options}
                    plugins={[ChartDataLabels, zoomPlugin]}
                    data={chartDataWithVisibility}
                    ref={chartRef}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </>
      )}
    </>
  )
}
